import {
  ApplicationIndividual,
  ApplicationIndividualRole,
  compareIdentity,
  IndividualWithResult,
  kycMergeDetailsMatch,
  kycMergeMismatchMessage,
} from '@portal-workspace/grow-shared-library';

const PROFILE_FIELDS: { label: string; read: (individual: ApplicationIndividual) => string | null | undefined }[] = [
  { label: 'Title', read: i => i.title },
  { label: 'Given name', read: i => i.givenName },
  { label: 'Middle name', read: i => i.middleName },
  { label: 'Surname', read: i => i.surname },
  { label: 'Gender', read: i => i.gender },
  { label: 'Date of birth', read: i => i.dateOfBirth },
  { label: 'Email', read: i => i.email },
  { label: 'Mobile number', read: i => i.mobileNumber },
  { label: 'Country of citizenship', read: i => i.countryOfCitizenship },
];

export interface MergeProfileRow {
  label: string;
  /** What the merged person will carry. */
  result: string;
  /** Values on the other people that the merge discards. */
  discarded: string[];
  /** The survivor was blank and takes the single value held by the others. */
  filled: boolean;
}

const clean = (value: string | null | undefined): string => (value ?? '').trim();
const key = (value: string): string => value.toLowerCase();

/**
 * The server keeps the survivor's value, fills a blank only when the others agree, and leaves a
 * blank alone when they disagree. This mirrors that so staff see the outcome before confirming.
 * Only rows where the merge changes or discards something are returned.
 */
export function previewProfileMerge(survivor: ApplicationIndividual, others: ApplicationIndividual[]): MergeProfileRow[] {
  const rows: MergeProfileRow[] = [];
  for (const field of PROFILE_FIELDS) {
    const own = clean(field.read(survivor));
    const theirs = others.map(other => clean(field.read(other))).filter(Boolean);
    const distinct = [...new Set(theirs.map(key))];
    if (own) {
      const discarded = [...new Set(theirs.filter(value => key(value) !== key(own)))];
      if (discarded.length) rows.push({ label: field.label, result: own, discarded, filled: false });
    } else if (distinct.length === 1) {
      rows.push({ label: field.label, result: theirs[0], discarded: [], filled: true });
    } else if (distinct.length > 1) {
      rows.push({ label: field.label, result: '', discarded: [...new Set(theirs)], filled: false });
    }
  }
  return rows;
}

/** Higher is more complete. Verified KYC outranks signed consent outranks a Salesforce contact. */
export function completenessScore(individual: IndividualWithResult): number {
  const verified = individual.result?.status === 'RECEIVED_COMPLETE' ? 4 : 0;
  const consented = (individual.privacyConsent ?? []).some(consent => consent.status === 'accepted') ? 2 : 0;
  const salesforce = individual.sfContactId ? 1 : 0;
  return verified + consented + salesforce;
}

/** The most complete person; the oldest record wins a tie so the choice is stable across refreshes. */
export function pickDefaultSurvivor(people: IndividualWithResult[]): IndividualWithResult | null {
  return people.reduce<IndividualWithResult | null>((best, person) => {
    if (!best) return person;
    const delta = completenessScore(person) - completenessScore(best);
    return delta > 0 || (delta === 0 && person.id < best.id) ? person : best;
  }, null);
}

const ROLE_FLAGS = ['isDirector', 'isShareholder', 'isTrustee', 'isPartner', 'isSoleTrader', 'isSpouse', 'isSigner'] as const;

const KYC_STATUS_RANK: Record<string, number> = {
  RECEIVED_COMPLETE: 6,
  RECEIVED_PENDING_REVIEW: 5,
  RECEIVED_INCOMPLETE: 4,
  RECEIVED_FAILED: 3,
  RECEIVED_ERROR: 2,
  SMS_SENT: 1,
  WAITING: 0,
};
const kycRank = (individual: IndividualWithResult): number => KYC_STATUS_RANK[individual.result?.status ?? ''] ?? -1;

/** Whose KYC result the server keeps: the best status, the survivor's own on a tie. */
const keptResultHolder = (survivor: IndividualWithResult, others: IndividualWithResult[]): IndividualWithResult =>
  others.reduce((best, person) => (kycRank(person) > kycRank(best) ? person : best), survivor);

/**
 * The survivor as the server will leave it: roles on the same company fold into one with their flags
 * combined, the guarantor and primary-contact flags carry over, a blank Salesforce contact is adopted,
 * and the survivor keeps its own verification unless it has none, in which case the best one moves.
 * Consent forms are not moved, so only the survivor's own count.
 */
export function previewMergedIndividual(survivor: IndividualWithResult, others: IndividualWithResult[]): IndividualWithResult {
  const everyone = [survivor, ...others];
  const roles = new Map<string, ApplicationIndividualRole>();
  everyone.flatMap(person => person.roles ?? []).forEach(role => {
    const key = role.entityId == null ? 'none' : String(role.entityId);
    const existing = roles.get(key);
    if (!existing) {
      roles.set(key, { ...role });
      return;
    }
    ROLE_FLAGS.forEach(flag => { existing[flag] = !!existing[flag] || !!role[flag]; });
    existing.isPrimaryRole = !!existing.isPrimaryRole || !!role.isPrimaryRole;
  });
  return {
    ...survivor,
    roles: [...roles.values()],
    isGuarantor: everyone.some(person => person.isGuarantor),
    isPrimaryContact: everyone.some(person => person.isPrimaryContact),
    sfContactId: survivor.sfContactId || others.find(other => other.sfContactId)?.sfContactId || survivor.sfContactId,
    result: keptResultHolder(survivor, others).result,
    privacyConsent: survivor.privacyConsent ?? [],
  };
}

/** A discarded person's signed consent stays on their record, so the kept one would be left without one. */
export function keptRecordLosesSignedConsent(survivor: IndividualWithResult, others: IndividualWithResult[]): boolean {
  const hasSigned = (individual: IndividualWithResult) => (individual.privacyConsent ?? []).some(consent => consent.status === 'accepted');
  return !hasSigned(survivor) && others.some(hasSigned);
}

const REQUIRED_DETAILS: { label: string; read: (individual: ApplicationIndividual) => string }[] = [
  { label: 'Title', read: i => clean(i.title) },
  { label: 'First name', read: i => clean(i.givenName) },
  { label: 'Last name', read: i => clean(i.surname) },
  { label: 'Date of birth', read: i => clean(i.dateOfBirth) },
  { label: 'Country of citizenship', read: i => clean(i.countryOfCitizenship) },
  { label: 'Residential address', read: i => clean(i.homeAddress?.unformattedAddress) },
  { label: 'Email', read: i => clean(i.email) },
  { label: 'Mobile', read: i => clean(i.mobileNumber) },
  {
    label: 'Identification',
    read: i => (clean(i.identification?.type) && clean(i.identification?.number)
      ? `${clean(i.identification?.type)}|${clean(i.identification?.number)}` : ''),
  },
];

/**
 * What the merged person will still lack, by the same list the individuals table's needs-attention
 * tooltip uses. A blank on the survivor is filled only when the others agree on a single value,
 * exactly as the server does, so two different candidates leave it blank.
 */
export function missingDetailsAfterMerge(
  survivor: ApplicationIndividual,
  others: ApplicationIndividual[],
  anyIdDocumentOnFile: boolean,
): string[] {
  const missing = REQUIRED_DETAILS
    .filter(field => {
      if (field.read(survivor)) return false;
      const candidates = new Set(others.map(other => key(field.read(other))).filter(Boolean));
      return candidates.size !== 1;
    })
    .map(field => field.label);
  if (!anyIdDocumentOnFile) missing.push('ID document');
  return missing;
}

/**
 * Why a record does not look like the same person as the one being kept: a name the shared
 * `compareIdentity` rule does not see as the same (two different middle names included), or a
 * different date of birth. Only a value both records hold can disagree, so a bare record with no date
 * of birth is never flagged for it. A warning for the reviewer only; it is not a rule the server enforces.
 */
export function identityMismatches(survivor: ApplicationIndividual, other: ApplicationIndividual): string[] {
  const reasons: string[] = [];
  const nameMatch = compareIdentity(survivor, other);
  if (nameMatch === 'none' || nameMatch === 'partial') reasons.push('name');
  const survivorDob = clean(survivor.dateOfBirth).slice(0, 10);
  const otherDob = clean(other.dateOfBirth).slice(0, 10);
  if (survivorDob && otherDob && survivorDob !== otherDob) reasons.push('date of birth');
  return reasons;
}

/** The survivor's value, or the one value the others agree on when the survivor's is blank. */
const valueAfterMerge = (survivor: ApplicationIndividual, others: ApplicationIndividual[], read: (i: ApplicationIndividual) => string | null | undefined): string => {
  const own = read(survivor) ?? '';
  if (own.trim()) return own;
  const theirs = others.map(other => read(other) ?? '').filter(value => value.trim());
  return new Set(theirs.map(value => key(value.trim()))).size === 1 ? theirs[0] : '';
};

const personName = (individual: ApplicationIndividual): string =>
  [individual.givenName, individual.surname].map(clean).filter(Boolean).join(' ') || `Person ${individual.id}`;

/**
 * The KYC result the server keeps checked against the kept record the way the server checks it. On a
 * mismatch the server moves KYC to Under review with this same message, so staff read it first here.
 */
export function kycDetailsMismatch(survivor: IndividualWithResult, others: IndividualWithResult[]): string | null {
  const holder = keptResultHolder(survivor, others);
  const result = holder.result;
  if (result?.status !== 'RECEIVED_COMPLETE' || !('digitalIdResponse' in result)) return null;
  const response = result.digitalIdResponse;
  if (response?.verificationMethod === 'Manual Verification') return null;
  const verified = response?.frankieOneCallback?.verifiedPersonDetails;
  if (!verified?.firstName || !verified.surname || !verified.dateOfBirth) return null;
  const kept = {
    firstName: valueAfterMerge(survivor, others, i => i.givenName),
    surname: valueAfterMerge(survivor, others, i => i.surname),
    dateOfBirth: valueAfterMerge(survivor, others, i => i.dateOfBirth).trim().slice(0, 10),
  };
  const verifiedDetails = { firstName: verified.firstName, surname: verified.surname, dateOfBirth: verified.dateOfBirth };
  if (kycMergeDetailsMatch(kept, verifiedDetails)) return null;
  return kycMergeMismatchMessage(kept, verifiedDetails);
}

export interface MergeSalesforceContact {
  id: string;
  name: string;
  kept: boolean;
}

/**
 * Every distinct Salesforce contact on the people being merged, kept one first. The merged person
 * stays on the survivor's contact, or adopts one when the survivor has none. Empty unless there are two.
 */
export function salesforceContactsInMerge(survivor: IndividualWithResult, others: IndividualWithResult[]): MergeSalesforceContact[] {
  const keptId = survivor.sfContactId || others.find(other => other.sfContactId)?.sfContactId;
  const contacts = new Map<string, MergeSalesforceContact>();
  [survivor, ...others].forEach(person => {
    if (person.sfContactId && !contacts.has(person.sfContactId)) {
      contacts.set(person.sfContactId, { id: person.sfContactId, name: personName(person), kept: person.sfContactId === keptId });
    }
  });
  const list = [...contacts.values()].sort((a, b) => Number(b.kept) - Number(a.kept));
  return list.length > 1 ? list : [];
}

export function describeRole(role: ApplicationIndividualRole): string {
  const flags = [
    role.isDirector && 'Director',
    role.isShareholder && 'Shareholder',
    role.isTrustee && 'Trustee',
    role.isPartner && 'Partner',
    role.isSoleTrader && 'Sole trader',
    role.isSpouse && 'Spouse',
    role.isSigner && 'Signer',
  ].filter(Boolean);
  const entity = role.entityLegalName ?? 'Application';
  return flags.length ? `${entity}: ${flags.join(', ')}` : entity;
}
